import {makeStyles} from "@material-ui/core"
import {ModernTheme} from "../../../../../theme/modern/tokens"
import {COARSE, TOUCH_MIN, touchTarget} from "../../../../../theme/modern/touchTargets"

/**
 * Ported from the prototype's `pages/Login/login.css` — the `.login-shell`,
 * `.login-topbar` and `.login-aside` rule sets, plus the moon / language pill
 * chrome the prototype reuses from its app bar.
 *
 * The one place a single token set cannot express both palettes is the aside
 * gradient: light paints a bright brand gradient, reading-room paints a nearly
 * black one. Rather than a forbidden `html[data-theme="dark"] &` selector, the
 * dark variant is a `__dark` modifier the component toggles from
 * `useUiSkin().isDark`, declared AFTER `aside` so it wins on source order.
 */
export default makeStyles((theme: ModernTheme) => {
    const brand = `linear-gradient(150deg, ${theme.modern.accent} 0%, #143FB0 100%)`;
    const readingRoom = `linear-gradient(150deg, ${theme.modern.accentSoft} 0%, ${theme.modern.bg} 100%)`;

    return ({
    /**
     * Two columns on a desktop; brand panel STACKED ABOVE the card on a phone.
     *
     * The aside used to be `display:none` under 880 px, which left a mobile
     * reviewer with a bare card on a grey field and no branding anywhere —
     * <ModernSignInForm/> carried its own wordmark to cover for it, which is
     * exactly why there were two logos side by side on a desktop. The panel now
     * survives at every width as a banner and the card's copy of the wordmark
     * is gone.
     *
     * `auto 1fr` rather than two auto rows: the second row keeps whatever height
     * is left so the page background fills a tall viewport, and <ModernShell/>
     * is min-height:100vh so a short viewport simply scrolls. The card does NOT
     * centre itself in that row on a phone — see `main`.
     */
    shell: {
        position: "relative",
        flex: "1 1 auto",
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: "1.1fr 1fr",
        background: theme.modern.bg,
        "@media (max-width:880px)": {
            gridTemplateColumns: "1fr",
            gridTemplateRows: "auto auto 1fr",
            background: brand,
        },
    },
    shell__dark: {
        "@media (max-width:880px)": {background: readingRoom},
    },

    // ---- floating top bar (reading-room + language, no app bar pre-auth) ----
    topbar: {
        position: "absolute",
        top: 16,
        right: 18,
        zIndex: 5,
        display: "flex",
        alignItems: "center",
        gap: "10px",

        "@media (max-width:880px)": {
            position: "static",
            justifySelf: "end",
            margin: "10px 12px 0",
            gap: "6px",
            padding: "4px 6px",
            borderRadius: 10,
            background: theme.modern.panel,
            border: `1px solid ${theme.modern.line}`,
            boxShadow: theme.modern.shadowSegment,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            rowGap: 4,
        },
    },

    // ---- Classic | Modern, the app bar's control restated in this file ----
    uimode: {
        display: "inline-flex",
        alignItems: "center",
        background: theme.modern.line2,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 8,
        padding: 2,
        gap: "2px",
    },
    uimode_btn: {
        font: `600 11.5px ${theme.modern.sans}`,
        color: theme.modern.ink2,
        background: "none",
        border: "none",
        padding: "4px 10px",
        borderRadius: 6,
        cursor: "pointer",
        transition: "background .15s, color .15s",
        whiteSpace: "nowrap",

        "&:hover": {color: theme.modern.ink},
        [COARSE]: touchTarget({padding: "0 12px"}),
    },
    uimode_btn__active: {
        background: theme.modern.panel,
        color: theme.modern.accent,
        boxShadow: theme.modern.shadowSegment,

        "&:hover": {color: theme.modern.accent},
    },
    iconbtn: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 34,
        height: 30,
        padding: 0,
        borderRadius: 7,
        border: `1px solid ${theme.modern.line}`,
        background: theme.modern.panel,
        color: theme.modern.ink2,
        cursor: "pointer",

        "&:hover": {
            borderColor: theme.modern.ink3,
            color: theme.modern.ink,
        },

        "& svg": {width: 14, height: 14, flex: "none"},

        [COARSE]: {width: TOUCH_MIN, height: TOUCH_MIN},
    },
    iconbtn__active: {
        borderColor: theme.modern.accentLine,
        background: theme.modern.accentSoft,
        color: theme.modern.accent,

        "&:hover": {color: theme.modern.accent},
    },
    lang: {
        display: "inline-flex",
        alignItems: "center",
        gap: "1px",
        whiteSpace: "nowrap",
    },
    langbtn: {
        font: `600 11px ${theme.modern.mono}`,
        color: theme.modern.ink3,
        background: "none",
        border: "none",
        padding: "3px 7px",
        borderRadius: 5,
        letterSpacing: ".03em",
        cursor: "pointer",

        "&:hover": {
            color: theme.modern.ink,
            background: theme.modern.line2,
        },
    },
    langbtn__active: {
        color: theme.modern.accent,
        background: theme.modern.accentSoft,
    },

    // ---- left decorative panel ----
    aside: {
        position: "relative",
        overflow: "hidden",
        background: brand,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 40,

        // A banner rather than a column: top-aligned, and left-aligned with the
        "@media (max-width:880px)": {
            alignItems: "flex-start",
            justifyContent: "flex-start",
            padding: "26px 24px 24px",
            background: "none",
        },
        "@media (max-width:520px)": {padding: "22px 20px 20px"},
    },
    aside__dark: {
        background: readingRoom,
        "@media (max-width:880px)": {background: "none"},
    },
    aside_inner: {
        maxWidth: 420,
        width: "100%",

        "@media (max-width:880px)": {maxWidth: "none"},
    },
    aside_brand: {
        color: "#fff",
        opacity: 0.96,
        display: "block",
        width: "auto",

        // BrandMark sizes from its `height` prop, which is an attribute rather
        // than a style, so the banner has to override it here.
        "@media (max-width:520px)": {height: 24},
    },
    aside_tag: {
        marginTop: 18,
        marginBottom: 0,
        font: `500 16px/1.5 ${theme.modern.sans}`,
        color: "rgba(255,255,255,.86)",

        "@media (max-width:880px)": {marginTop: 10, fontSize: 14},
    },
    aside_ecg: {
        marginTop: 34,
        display: "block",
        width: "100%",
        height: 160,
        color: "rgba(255,255,255,.7)",

        // The trace is the one part of the panel that can give up its height
        // without the banner stopping being the brand — it is decorative, and
        // on a phone every pixel it keeps pushes the password field further
        // down the page.
        "@media (max-width:880px)": {marginTop: 16, height: 72},
        "@media (max-width:520px)": {marginTop: 14, height: 54},
    },

    // ---- right form panel ----
    main: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
        "@media (max-width:880px)": {
            alignItems: "flex-start",
            padding: "28px 20px 40px",
        },
    },
})})
